import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../../components/ui/button";
import { DifficultySelector, GameOverScreen } from "./GameShell";
import { Check } from "lucide-react";

const DIFFICULTY_CONFIG = {
  easy:   { ops: ["+", "-"], maxNum: 20, time: 60, bonusMult: 1 },
  medium: { ops: ["+", "-", "\u00d7"], maxNum: 50, time: 60, bonusMult: 1.5 },
  hard:   { ops: ["+", "-", "\u00d7", "\u00f7"], maxNum: 99, time: 45, bonusMult: 2 },
};

const TUTORIAL_STEPS = [
  { visual: "🔢", title: "Solve Math Problems", description: "Simple arithmetic problems appear on screen. Type your answer and hit Enter!" },
  { visual: "⏱️", title: "Beat the Clock", description: "You have limited time. Answer as many as possible before time runs out." },
  { visual: "🔥", title: "Build a Streak", description: "Consecutive correct answers build a streak multiplier for bonus points." },
];

const MathSprintGame = ({ onComplete, onBack, t }) => {
  const [difficulty, setDifficulty] = useState(null);
  const [problem, setProblem] = useState(null);
  const [answer, setAnswer] = useState("");
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [gameOver, setGameOver] = useState(false);
  const [streak, setStreak] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [total, setTotal] = useState(0);
  const [config, setConfig] = useState(null);
  const [flash, setFlash] = useState(null); // "correct" | "wrong" | null
  const inputRef = useRef(null);
  const gameOverRef = useRef(false);
  const scoreRef = useRef(0);
  const correctRef = useRef(0);
  const totalRef = useRef(0);

  const generateProblem = useCallback((cfg) => {
    const op = cfg.ops[Math.floor(Math.random() * cfg.ops.length)];
    let a, b, ans;

    if (op === "+") {
      a = Math.floor(Math.random() * cfg.maxNum) + 1;
      b = Math.floor(Math.random() * cfg.maxNum) + 1;
      ans = a + b;
    } else if (op === "-") {
      a = Math.floor(Math.random() * cfg.maxNum) + 10;
      b = Math.floor(Math.random() * a);
      ans = a - b;
    } else if (op === "\u00d7") {
      a = Math.floor(Math.random() * 12) + 2;
      b = Math.floor(Math.random() * 12) + 2;
      ans = a * b;
    } else {
      // Division - ensure clean result
      b = Math.floor(Math.random() * 11) + 2;
      ans = Math.floor(Math.random() * 12) + 1;
      a = b * ans;
    }

    setProblem({ a, b, op, answer: ans });
    setAnswer("");
  }, []);

  const startGame = useCallback((diff) => {
    const cfg = DIFFICULTY_CONFIG[diff];
    setDifficulty(diff);
    setConfig(cfg);
    setScore(0);
    setTimeLeft(cfg.time);
    setGameOver(false);
    setStreak(0);
    setCorrect(0);
    setTotal(0);
    gameOverRef.current = false;
    scoreRef.current = 0;
    correctRef.current = 0;
    totalRef.current = 0;
    generateProblem(cfg);
  }, [generateProblem]);

  // Timer
  useEffect(() => {
    if (!config || gameOver) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setGameOver(true);
          gameOverRef.current = true;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [config, gameOver]);

  // On game over, report using refs to get current values
  useEffect(() => {
    if (gameOver && config) {
      const accuracy = totalRef.current > 0 ? Math.round((correctRef.current / totalRef.current) * 100) : 0;
      onComplete(scoreRef.current, Math.ceil(scoreRef.current / 50), accuracy, difficulty);
    }
  }, [gameOver, config, onComplete, difficulty]);

  const checkAnswer = () => {
    if (!problem || answer === "") return;
    setTotal((t) => { totalRef.current = t + 1; return t + 1; });

    if (parseInt(answer, 10) === problem.answer) {
      const streakBonus = streak * 2;
      const pts = Math.round((10 + streakBonus) * config.bonusMult);
      setScore((s) => { scoreRef.current = s + pts; return s + pts; });
      setStreak((s) => s + 1);
      setCorrect((c) => { correctRef.current = c + 1; return c + 1; });
      setFlash("correct");
    } else {
      setStreak(0);
      setFlash("wrong");
    }
    setTimeout(() => setFlash(null), 300);
    generateProblem(config);
    // Re-focus input
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  if (!difficulty) {
    return (
      <DifficultySelector
        gameName={t("games.mathSprint.name")}
        gameDescription={t("games.mathSprint.description")}
        tutorialSteps={TUTORIAL_STEPS}
        onStart={startGame}
        onBack={onBack}
        t={t}
      />
    );
  }

  if (gameOver) {
    const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;
    return (
      <GameOverScreen
        score={score}
        subtitle={`${correct}/${total} correct (${accuracy}%) | ${t("games.difficulty." + difficulty)}`}
        onPlayAgain={() => startGame(difficulty)}
        onBack={onBack}
        t={t}
      />
    );
  }

  const timePercent = config ? (timeLeft / config.time) * 100 : 100;

  return (
    <div className="text-center" data-testid="math-game-active">
      {/* HUD */}
      <div className="flex justify-between mb-4">
        <div>
          <p className="text-sm text-slate-500">{t("common.score")}</p>
          <p className="text-xl font-medium text-sky-800">{score}</p>
        </div>
        <div>
          <p className="text-sm text-slate-500">Time</p>
          <p className={`text-xl font-medium ${timeLeft <= 10 ? "text-red-500 animate-pulse" : "text-sky-800"}`}>
            {timeLeft}s
          </p>
        </div>
        <div>
          <p className="text-sm text-slate-500">{t("games.streak")}</p>
          <p className="text-xl font-medium text-amber-500">{streak > 0 ? `${streak}x` : "0"}</p>
        </div>
      </div>

      {/* Timer bar */}
      <div className="w-full h-1.5 bg-slate-200 rounded-full mb-6 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-1000 ${timeLeft <= 10 ? "bg-red-500" : "bg-sky-400"}`}
          style={{ width: `${timePercent}%` }}
        />
      </div>

      {/* Problem */}
      {problem && (
        <div
          className={`glass-card p-8 mb-6 transition-colors duration-200 ${
            flash === "correct" ? "ring-2 ring-emerald-400 bg-emerald-50/50" :
            flash === "wrong" ? "ring-2 ring-red-400 bg-red-50/50" : ""
          }`}
        >
          <p className="text-4xl font-light text-sky-900 mb-6" style={{ fontFamily: "Outfit" }}>
            {problem.a} {problem.op} {problem.b} = ?
          </p>
          <div className="flex items-center justify-center gap-3">
            <input
              ref={inputRef}
              type="number"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && checkAnswer()}
              className="w-32 text-center text-2xl p-3 border-2 border-sky-200 rounded-xl focus:border-sky-400 outline-none text-blue-900"
              autoFocus
              data-testid="math-answer-input"
            />
            <Button onClick={checkAnswer} className="glow-button" data-testid="math-submit-btn">
              <Check className="w-5 h-5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MathSprintGame;
